import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const BASE_URL = API_URL.replace('/api', '');

export const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('data:image')) return path;
    if (path.startsWith('http')) return path;
    return `${BASE_URL}${path}`;
};

// Helper to get auth header
const authHeader = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.token) {
        return { Authorization: `Bearer ${user.token}` };
    } else {
        return {};
    }
};

const getCustomers = () => {
    return axios.get(`${API_URL}/customers`, { headers: authHeader() });
};

const addCustomer = (customerData) => {
    return axios.post(`${API_URL}/customers`, customerData, { headers: authHeader() });
};

const deleteCustomer = (id) => {
    return axios.delete(`${API_URL}/customers/${id}`, { headers: authHeader() });
};

const updateCustomer = (id, data) => {
    return axios.put(`${API_URL}/customers/${id}`, data, { headers: authHeader() });
};

const getVendors = () => {
    return axios.get(`${API_URL}/vendors`, { headers: authHeader() });
};

const addVendor = (vendorData) => {
    return axios.post(`${API_URL}/vendors`, vendorData, { headers: authHeader() });
};

const deleteVendor = (id) => {
    return axios.delete(`${API_URL}/vendors/${id}`, { headers: authHeader() });
};

const updateVendor = (id, data) => {
    return axios.put(`${API_URL}/vendors/${id}`, data, { headers: authHeader() });
};

const getProducts = () => {
    return axios.get(`${API_URL}/products`, { headers: authHeader() });
};

const addProduct = (productData) => {
    return axios.post(`${API_URL}/products`, productData, { headers: authHeader() });
};

const deleteProduct = (id) => {
    return axios.delete(`${API_URL}/products/${id}`, { headers: authHeader() });
};

const updateProduct = (id, data) => {
    return axios.put(`${API_URL}/products/${id}`, data, { headers: authHeader() });
};

const deleteCategory = (name) => {
    return axios.delete(`${API_URL}/products/category/${encodeURIComponent(name)}`, { headers: authHeader() });
};

const getOrders = (type) => {
    const url = type ? `${API_URL}/orders?type=${type}` : `${API_URL}/orders`;
    return axios.get(url, { headers: authHeader() });
};

const getOrderById = (id) => {
    return axios.get(`${API_URL}/orders/${id}`, { headers: authHeader() });
};

const getOrdersByCustomer = (customerName) => {
    return axios.get(`${API_URL}/orders?customer=${encodeURIComponent(customerName)}`, { headers: authHeader() });
};

const addOrder = (orderData) => {
    return axios.post(`${API_URL}/orders`, orderData, { headers: authHeader() });
};

const deleteOrder = (id) => {
    return axios.delete(`${API_URL}/orders/${id}`, { headers: authHeader() });
};

const updateOrder = (id, data) => {
    return axios.put(`${API_URL}/orders/${id}`, data, { headers: authHeader() });
};

const getNextInvoiceNumber = () => {
    return axios.get(`${API_URL}/orders/next-number`, { headers: authHeader() });
};

const getExpenses = () => {
    return axios.get(`${API_URL}/expenses`, { headers: authHeader() });
};

const addExpense = (expenseData) => {
    return axios.post(`${API_URL}/expenses`, expenseData, { headers: authHeader() });
};

const deleteExpense = (id) => {
    return axios.delete(`${API_URL}/expenses/${id}`, { headers: authHeader() });
};

const updateExpense = (id, data) => {
    return axios.put(`${API_URL}/expenses/${id}`, data, { headers: authHeader() });
};

const getPurchases = () => {
    return axios.get(`${API_URL}/purchases`, { headers: authHeader() });
};

const addPurchase = (purchaseData) => {
    return axios.post(`${API_URL}/purchases`, purchaseData, { headers: authHeader() });
};

const deletePurchase = (id) => {
    return axios.delete(`${API_URL}/purchases/${id}`, { headers: authHeader() });
};

const updatePurchase = (id, data) => {
    return axios.put(`${API_URL}/purchases/${id}`, data, { headers: authHeader() });
};

const getPayments = () => {
    return axios.get(`${API_URL}/payments`, { headers: authHeader() });
};

const recordPayment = (paymentData) => {
    return axios.post(`${API_URL}/payments`, paymentData, { headers: authHeader() });
};

const deletePayment = (id) => {
    return axios.delete(`${API_URL}/payments/${id}`, { headers: authHeader() });
};

const updatePayment = (id, data) => {
    return axios.put(`${API_URL}/payments/${id}`, data, { headers: authHeader() });
};

const getReportSummary = () => {
    return axios.get(`${API_URL}/reports/summary`, { headers: authHeader() });
};

const getSalesReport = () => {
    return axios.get(`${API_URL}/reports/sales`, { headers: authHeader() });
};

const getInventoryReport = () => {
    return axios.get(`${API_URL}/reports/inventory`, { headers: authHeader() });
};

const getProfitPerProduct = () => {
    return axios.get(`${API_URL}/reports/profit-product`, { headers: authHeader() });
};

const getTeamPerformanceReport = (period) => {
    return axios.get(`${API_URL}/reports/team-performance${period ? `?period=${period}` : ''}`, { headers: authHeader() });
};

const dataService = {
    getCustomers,
    addCustomer,
    deleteCustomer,
    updateCustomer,
    getVendors,
    addVendor,
    deleteVendor,
    updateVendor,
    getProducts,
    addProduct,
    deleteProduct,
    updateProduct,
    getOrders,
    getOrderById,
    addOrder,
    deleteOrder,
    updateOrder,
    getOrdersByCustomer,
    getExpenses,
    addExpense,
    deleteExpense,
    updateExpense,
    getPurchases,
    addPurchase,
    deletePurchase,
    updatePurchase,
    getPayments,
    recordPayment,
    deletePayment,
    updatePayment,
    getReportSummary,
    getSalesReport,
    getInventoryReport,
    getProfitPerProduct,
    getUsers: () => {
        return axios.get(`${API_URL}/users`, { headers: authHeader() });
    },
    addUser: (userData) => {
        return axios.post(`${API_URL}/users`, userData, { headers: authHeader() });
    },
    updateUser: (id, data) => {
        return axios.put(`${API_URL}/users/${id}`, data, { headers: authHeader() });
    },
    deleteUser: (id) => {
        return axios.delete(`${API_URL}/users/${id}`, { headers: authHeader() });
    },
    getSalesPersons: () => {
        return axios.get(`${API_URL}/salespersons`, { headers: authHeader() });
    },
    addSalesPerson: (data) => {
        return axios.post(`${API_URL}/salespersons`, data, { headers: authHeader() });
    },
    deleteSalesPerson: (id) => {
        return axios.delete(`${API_URL}/salespersons/${id}`, { headers: authHeader() });
    },
    getTeamPerformanceReport,
    getImageUrl,
    getNextInvoiceNumber,
    deleteCategory,
};

export default dataService;
